<template>
    <div class="counterparts_card">
        <div class="card_header is-flex is-align-items-center">
            <PieChartIcon class="header_icon" />
            <span class="card_title">Contrapartes</span>
        </div>

        <div class="search_wrap">
            <svg class="search_icon" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round"
                aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.35-4.35" />
            </svg>
            <input type="search"
                class="search_input"
                :value="value"
                placeholder="Buscar Contrapartes"
                @input="$emit('input', $event.target.value)" />
        </div>

        <ul v-if="counterparts.length > 0" class="ranking_list">
            <li v-for="cp in counterparts" :key="cp.name + '|' + (cp.counterpart_type || '')"
                class="ranking_item"
                role="button"
                tabindex="0"
                @click="onCounterpartClick(cp)"
                @keydown.enter.prevent="onCounterpartClick(cp)"
                @keydown.space.prevent="onCounterpartClick(cp)">
                <span class="ranking_label"
                    v-tooltip.top="cp.name.length > 18 ? cp.name : ''">
                    {{ cp.name }}
                </span>
                <!-- Wrapper neutro: el track del grid es fijo (120px) para que
                    las barras queden parejas, pero la pill no debe estirarse a
                    ese ancho (los grid items hacen stretch por default y el
                    fondo quedaba con aire tras el texto). El wrapper absorbe el
                    stretch y la pill conserva su ancho natural. -->
                <span class="ranking_type">
                    <CounterpartTypePill v-if="cp.counterpart_type"
                        :type="cp.counterpart_type" />
                </span>
                <div class="ranking_bar_track">
                    <div class="ranking_bar_fill"
                        :style="{ width: barWidth(cp.share) }"></div>
                </div>
                <span class="ranking_meta">
                    <span class="ranking_amount">{{ formatMoney(cp.amount) }}</span>
                    <span class="ranking_dot" aria-hidden="true">·</span>
                    <span class="ranking_share">{{ formatShare(cp.share) }}</span>
                </span>
            </li>
        </ul>

        <p v-else class="empty_state">
            Sin resultados para "{{ value }}".
        </p>
    </div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import PieChartIcon from "@/components/svg/PieChartIcon.vue";
import CounterpartTypePill from "@/components/generics/CounterpartTypePill.vue";

export default {
    name: "CounterpartsRankingCard",
    components: { PieChartIcon, CounterpartTypePill },
    // v-model: value/input. El padre (BodyCostsAnalysis) hace el bridge con el
    // store. La card queda presentational — los specs no necesitan Vuex.
    model: { prop: "value", event: "input" },
    props: {
        counterparts: {
            type: Array,
            required: true,
            validator: (arr) => arr.every((c) => typeof c.name === "string"),
        },
        value: { type: String, default: "" },
    },
    methods: {
        sharePercent(share) {
            const v = Math.max(0, Math.min(1, share ?? 0));
            return Math.round(v * 100);
        },
        barWidth(share) {
            return `${this.sharePercent(share)}%`;
        },
        formatMoney(v) {
            return parseToMoney(v);
        },
        formatShare(share) {
            return `${this.sharePercent(share)}%`;
        },
        // Drill-down: el caller abre el detalle de la contraparte (documentType
        // 'P', level_2 'Costos'). El evento lleva la contraparte completa.
        onCounterpartClick(counterpart) {
            this.$emit("counterpart-click", counterpart);
        },
    },
};
</script>

<style scoped lang="scss">
.counterparts_card {
    background: var(--report-bg);
    border-radius: 16px;
    border: 1px solid var(--report-border);
    box-shadow: var(--report-shadow-card);
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.card_header {
    margin-bottom: 12px;
    color: var(--report-text);
    flex-shrink: 0;

    .header_icon {
        margin-right: 8px;
        flex-shrink: 0;
    }

    .card_title {
        font-size: 14px;
        font-weight: 600;
    }
}

.search_wrap {
    position: relative;
    margin-bottom: 12px;
    flex-shrink: 0;

    .search_icon {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        width: 16px;
        height: 16px;
        color: var(--report-text-muted-soft);
        pointer-events: none;
    }
}

.search_input {
    width: 100%;
    padding: 10px 12px 10px 38px;
    border: 1px solid var(--report-border);
    border-radius: 999px;
    background: var(--report-bg);
    font-size: 14px;
    color: var(--report-text);
    box-sizing: border-box;

    &::placeholder {
        color: var(--report-text-muted-soft);
    }

    &:focus {
        outline: none;
        border-color: var(--report-accent-text);
        box-shadow: 0 0 0 3px var(--report-accent-bg);
    }
}

.ranking_list {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.ranking_item {
    display: grid;
    // 4 columnas: label / badge de tipo / barra / meta. Los anchos son FIJOS
    // a propósito: cada <li> es su propio grid, así que cualquier track
    // auto/minmax se resuelve POR FILA — pills y montos de distinto ancho
    // empujaban la barra y el ranking quedaba escalonado. Con tracks fijos,
    // todas las barras comparten inicio y largo exactos. 120px cubre la pill
    // más ancha (Internacional); 140px, un monto CLP de 9 dígitos + share.
    grid-template-columns: 132px 120px 1fr 140px;
    gap: 12px;
    align-items: center;
    padding: 6px 8px;
    font-size: 13px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 120ms ease;

    &:hover {
        background: var(--report-surface);
    }

    &:focus-visible {
        outline: 2px solid var(--report-accent-border);
        outline-offset: 2px;
        background: var(--report-surface);
    }
}

.ranking_label {
    color: var(--report-text);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

// La pill de tipo de contraparte (color + ícono + label) vive en el componente
// transversal <CounterpartTypePill> (components/generics). Acá sólo se ubica en
// su columna del grid de `.ranking_item`.

.ranking_bar_track {
    background: var(--report-progress-bg);
    border-radius: 999px;
    height: 13px;
    overflow: hidden;
}

.ranking_bar_fill {
    height: 100%;
    border-radius: 999px;
    background: var(--report-chart-accent);
    transition: width 200ms ease-out;
}

.ranking_meta {
    display: inline-flex;
    align-items: baseline;
    // Alineado al borde derecho de su track fijo, así los montos quedan en
    // columna pareja y el espacio sobrante no desplaza la barra.
    justify-self: end;
    gap: 8px;
    color: var(--report-text-muted);
    font-size: 13px;
    white-space: nowrap;

    .ranking_amount {
        color: var(--report-text-muted-strong);
        font-weight: 500;
    }

    .ranking_dot {
        color: var(--report-text-muted-soft);
        font-weight: 700;
    }

    .ranking_share {
        color: var(--report-text-muted);
        font-weight: 500;
    }
}

.empty_state {
    color: var(--report-text-muted);
    font-size: 13px;
    text-align: center;
    padding: 24px 8px;
}
</style>
